import React from 'react';
import { Sparkles, Heart } from 'lucide-react';

// Shared renderer for section-based articles (opening + typed sections).
// Used by both Wellness and Whole Self resource pages.
const ArticleSections = ({ detailed }) => {
  if (!detailed) return null;
  return (
    <div className="space-y-8">
      {/* Opening */}
      {detailed.opening && (
        <div className="text-center py-4">
          {detailed.opening.split('\n\n').map((para, i) => (
            <p key={i} className={`font-body leading-relaxed mb-4 ${
              para.includes('"Where did I go?"')
                ? 'font-heading text-3xl sm:text-4xl text-terracotta italic'
                : 'text-stone-600 text-lg'
            }`}>{para.replace(/"/g, '')}</p>
          ))}
        </div>
      )}

      {/* Sections */}
      {detailed.sections?.map((section) => (
        <div key={section.id} className="relative">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-2.5 h-2.5 rounded-full bg-terracotta flex-shrink-0" />
            <h3 className="font-heading text-2xl text-forest">{section.title}</h3>
          </div>

          {/* Type: prose */}
          {section.type === 'prose' && (
            <div className="space-y-5">
              {section.paragraphs?.map((p, i) => (
                <p key={i} className="text-stone-600 font-body leading-relaxed">{p}</p>
              ))}
              {section.groups && (
                <div className="space-y-4">
                  {section.groups.map((g, gi) => (
                    <div
                      key={gi}
                      className={`rounded-xl p-6 border ${
                        g.tone === 'tcm'
                          ? 'bg-violet-50/60 border-violet-100'
                          : g.tone === 'caution'
                          ? 'bg-rose-50/50 border-rose-100'
                          : g.tone === 'neutral'
                          ? 'bg-stone-50 border-stone-100'
                          : 'bg-emerald-50/50 border-emerald-100'
                      }`}
                    >
                      <p className="font-body text-forest font-semibold mb-3">{g.title}</p>
                      <ul className="space-y-2.5">
                        {g.items.map((item, i) => (
                          <li key={i} className="flex gap-3 items-start">
                            <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" />
                            <span className="text-stone-600 font-body">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              {section.listItems && (
                <div className="bg-white rounded-xl p-6 border border-stone-100">
                  {section.listTitle && (
                    <p className="font-body text-forest font-medium mb-3">{section.listTitle}</p>
                  )}
                  <ul className="space-y-2.5">
                    {section.listItems.map((item, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" />
                        <span className="text-stone-600 font-body">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {section.callout && (
                <div className="bg-terracotta/5 rounded-xl p-6 border-l-4 border-terracotta">
                  {section.callout.split('\n').map((line, i) => (
                    <p key={i} className="text-forest font-body italic leading-relaxed">{line}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Type: intro-with-lists */}
          {section.type === 'intro-with-lists' && (
            <div className="space-y-5">
              {section.intro.split('\n\n').map((p, i) => (
                <p key={i} className="text-stone-600 font-body leading-relaxed">{p}</p>
              ))}
              <div className="bg-white rounded-xl p-6 border border-stone-100">
                <p className="font-body text-forest font-medium mb-3">{section.listTitle}</p>
                <ul className="space-y-2.5">
                  {section.listItems.map((item, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" />
                      <span className="text-stone-600 font-body">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {section.maskTitle && (
                <div className="bg-stone-50 rounded-xl p-6">
                  <p className="font-body text-forest font-medium mb-3">{section.maskTitle}</p>
                  <ul className="space-y-2.5">
                    {section.maskItems.map((item, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-2 flex-shrink-0" />
                        <span className="text-stone-600 font-body">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {section.closing && (
                <div className="bg-terracotta/5 rounded-xl p-6 border-l-4 border-terracotta">
                  {section.closing.split('\n').map((line, i) => (
                    <p key={i} className="text-forest font-body italic leading-relaxed">{line}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Type: nervous-system */}
          {section.type === 'nervous-system' && (
            <div className="space-y-5">
              {section.intro.split('\n').map((line, i) => (
                <p key={i} className={`font-body leading-relaxed ${
                  line.includes('depletion') ? 'text-forest font-medium text-lg' : 'text-stone-600'
                }`}>{line}</p>
              ))}
              <div className="bg-forest text-cream rounded-xl p-6">
                <p className="font-body text-cream/80 mb-3">{section.triggerTitle}</p>
                <ul className="space-y-2 mb-5">
                  {section.triggers.map((t, i) => (
                    <li key={i} className="flex gap-3 items-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0" />
                      <span className="font-body text-cream">{t}</span>
                    </li>
                  ))}
                </ul>
                {section.response.split('\n\n').map((p, i) => (
                  <p key={i} className="font-body text-cream/90 leading-relaxed mb-3">{p}</p>
                ))}
              </div>
              <div className="bg-terracotta/10 rounded-xl p-6 text-center">
                {section.truth.split('\n\n').map((p, i) => (
                  <p key={i} className={`font-body leading-relaxed mb-2 ${
                    p.includes("We don't have") ? 'font-heading text-xl text-terracotta italic' : 'text-forest'
                  }`}>{p}</p>
                ))}
              </div>
            </div>
          )}

          {/* Type: turning-point */}
          {section.type === 'turning-point' && (
            <div className="space-y-5">
              <p className="text-stone-600 font-body leading-relaxed">{section.content}</p>
              <div className="bg-stone-800 text-cream rounded-xl p-6 space-y-3">
                {section.innerCritic.map((line, i) => (
                  <p key={i} className="font-accent text-lg text-cream/90 italic text-center">{line}</p>
                ))}
              </div>
              <p className="text-stone-600 font-body leading-relaxed">{section.pushback}</p>
              <div className="bg-terracotta/10 rounded-xl p-6 text-center border-2 border-terracotta/20">
                {section.reframe.split('\n').map((line, i) => (
                  <p key={i} className="font-heading text-xl text-forest leading-relaxed">{line}</p>
                ))}
              </div>
            </div>
          )}

          {/* Type: shift */}
          {section.type === 'shift' && (
            <div className="space-y-5">
              {section.intro.split('\n\n').map((p, i) => (
                <p key={i} className="text-stone-600 font-body leading-relaxed">{p}</p>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {section.practices.map((practice, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-stone-100 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Sparkles size={14} className="text-emerald-600" />
                    </div>
                    <p className="text-stone-600 font-body text-sm leading-relaxed">{practice}</p>
                  </div>
                ))}
              </div>
              <div className="text-center py-4">
                {section.signal.split('\n\n').map((p, i) => (
                  <p key={i} className={`font-body leading-relaxed mb-2 ${
                    p.includes('allowed') ? 'font-heading text-xl text-forest italic' : 'text-stone-500'
                  }`}>{p}</p>
                ))}
              </div>
            </div>
          )}

          {/* Type: truth */}
          {section.type === 'truth' && (
            <div className="bg-gradient-to-br from-stone-50 to-terracotta/5 rounded-2xl p-8 space-y-3 text-center">
              {section.lines.map((line, i) => (
                <p key={i} className={`font-body leading-relaxed ${
                  i === section.lines.length - 1
                    ? 'font-heading text-xl text-terracotta italic mt-4 pt-4 border-t border-terracotta/20'
                    : i === section.lines.length - 2
                    ? 'text-forest font-medium'
                    : 'text-stone-600'
                }`}>{line}</p>
              ))}
            </div>
          )}

          {/* Type: passionate */}
          {section.type === 'passionate' && (
            <div className="space-y-5">
              {section.content.split('\n\n').map((p, i) => (
                <p key={i} className="text-stone-600 font-body leading-relaxed">{p}</p>
              ))}
              <div className="bg-forest text-cream rounded-2xl p-6 space-y-3">
                {section.realities.map((r, i) => (
                  <p key={i} className="font-body text-cream font-medium text-center leading-relaxed">{r}</p>
                ))}
              </div>
              <div className="bg-terracotta/10 rounded-xl p-6 border-l-4 border-terracotta">
                <p className="text-forest font-body leading-relaxed mb-3">{section.insight}</p>
                <p className="text-forest font-body font-medium leading-relaxed">{section.consequences}</p>
              </div>
            </div>
          )}

          {/* Type: cta */}
          {section.type === 'cta' && (
            <div className="bg-gradient-to-br from-forest to-forest-dark text-cream rounded-2xl p-8 text-center">
              <Heart size={28} className="text-terracotta mx-auto mb-4" />
              <p className="font-body text-cream text-lg leading-relaxed mb-6">{section.content}</p>
              <a
                href="mailto:heyjbennett@yahoo.ca"
                className="inline-block bg-terracotta text-white px-8 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors"
              >
                Reach Out to Jill
              </a>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ArticleSections;
